import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPE_LABEL = {
  question: "Soru",
  bug: "Hata Bildirimi",
  feature: "Özellik Önerisi",
  other: "Diğer",
};
const STATUS_LABEL = { investigating: "İnceleniyor", answered: "Cevaplandı" };

function StatusPill({ status }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
        status === "answered"
          ? "bg-emerald-100 text-emerald-800"
          : "bg-amber-100 text-amber-800"
      }`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("investigating");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support/tickets");
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Talepler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openTicket = (t) => {
    setSelected(t);
    setStatus(t.status || "investigating");
  };

  const onSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/support/tickets/${selected.id}`,
        { status },
      );
      setSelected(data);
      setTickets((prev) => prev.map((t) => (t.id === data.id ? data : t)));
      toast.success("Durum güncellendi.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Durum güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <PageHeader
        title="Destek Talepleri"
        subtitle="Firmalardan gelen talepleri görüntüle ve durumunu yönet"
      />

      <SectionCard title="Talepler">
        <div className="p-5" data-testid="admin-support-list">
          {loading ? (
            <div className="text-sm text-slate-500">Yükleniyor…</div>
          ) : tickets.length === 0 ? (
            <div className="text-sm text-slate-500">Henüz bir destek talebi yok.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 pr-4">Firma</th>
                    <th className="text-left py-2 pr-4">E-posta</th>
                    <th className="text-left py-2 pr-4">Tür</th>
                    <th className="text-left py-2 pr-4">Konu</th>
                    <th className="text-left py-2 pr-4">Tarih</th>
                    <th className="text-left py-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50"
                      onClick={() => openTicket(t)}
                      data-testid={`admin-support-row-${t.id}`}
                    >
                      <td className="py-2.5 pr-4 text-slate-800">{t.company_name || "—"}</td>
                      <td className="py-2.5 pr-4 text-slate-700">{t.user_email || "—"}</td>
                      <td className="py-2.5 pr-4 text-slate-700">{TYPE_LABEL[t.type] || t.type}</td>
                      <td className="py-2.5 pr-4 text-slate-800 max-w-xs truncate">{t.subject || "—"}</td>
                      <td className="py-2.5 pr-4 text-slate-600">
                        {t.created_at ? fmtDate(t.created_at.slice(0, 10)) : "—"}
                      </td>
                      <td className="py-2.5"><StatusPill status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SectionCard>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl" data-testid="admin-support-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.subject || "Talep"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {selected ? (
                <>
                  {selected.company_name || "—"} · {selected.user_email || "—"} ·{" "}
                  {TYPE_LABEL[selected.type] || selected.type} ·{" "}
                  {selected.created_at ? fmtDate(selected.created_at.slice(0, 10)) : "—"}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 whitespace-pre-wrap max-h-72 overflow-y-auto"
                 data-testid="admin-support-message">
              {selected?.message || ""}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Durum</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="admin-support-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="investigating">İnceleniyor</SelectItem>
                    <SelectItem value="answered">Cevaplandı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={onSave}
                disabled={saving || !selected || status === selected?.status}
                className="bg-slate-900 hover:bg-slate-800 rounded-lg h-10"
                data-testid="admin-support-save-status"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">
              Yanıt e-postası admin tarafından kendi mail hesabından gönderilir; durum "Cevaplandı" olarak işaretlendiğinde kullanıcı panelinde de güncellenir.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
